import { tap } from 'rxjs/operators';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UserFormComponent } from '../user-form/user-form.component';
import { Store } from '@ng-cloud/badger-core/models/store';
import { User } from '@ng-cloud/badger-core/models/user';
import { UserPreference } from '@ng-cloud/badger-core/models/user-preference';
import { UserService } from '@ng-cloud/badger-core/services/user.service';
import { OrganizationService } from '@ng-cloud/badger-core/services/organization.service';
import { AuthorizationService } from '@ng-cloud/badger-core/services/authorization.service';
import { StoreService } from '@ng-cloud/badger-core/services/store.service';
import { ThemeService } from '@ng-cloud/badger-core/services/theme.service';
import { TitleService } from '@ng-cloud/badger-core/services/title.service';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { TranslationLoaderService } from '../../translation/translation-loader.service';
import { LanguageService } from '@ng-cloud/badger-core/services/language.service';

import { ToastService } from '@ng-cloud/badger-core/toastr/toastr.service';

type LangOption = {
  code: string;
  label: string;
  flag: string;
};

@Component({
  selector: 'bt-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class UserDetailComponent implements OnInit {
  id: number;
  user: User;
  currentUserId: number;
  roleOptions: string[];
  currentTheme: string;
  userHomeStore: Store;
  userPreferences: UserPreference;

  allLangOptions: LangOption[] = [
    { code: 'en', label: 'English', flag: 'us' },
    { code: 'de', label: 'Deutsch', flag: 'de' },
    { code: 'es', label: 'Español', flag: 'es' },
    { code: 'fr', label: 'Français', flag: 'fr' }
  ];

  supportedLangs: LangOption[] = [];

  currentLang: LangOption;

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private userService: UserService,
    private organizationService: OrganizationService,
    private authService: AuthorizationService,
    private storeService: StoreService,
    private themeService: ThemeService,
    private titleService: TitleService,
    private router: Router,
    private translate: TranslateService,
    private translationLoader: TranslationLoaderService,
    private languageService: LanguageService,
    private toastr: ToastService
  ) {
    this.themeService.themes().subscribe(theme => {
      this.currentTheme = theme;
    });

    const allowedLangs = this.translationLoader.getAvailableLangs();

    this.supportedLangs = this.allLangOptions.filter(lang =>
      allowedLangs.includes(lang.code as any)
    );

     // Set current language from UserPreference. If it does not exists, use 'en' instead.
    this.languageService.langs().subscribe(lang => {
      this.currentLang = this.supportedLangs.find(supportedLang => supportedLang.code === lang) || this.supportedLangs[0];
      this.translate.use(this.currentLang.code);
    })
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.authService.validateToken().subscribe(() => {
        this.authService.getCurrentUser().subscribe(currentUserData => {
          if (currentUserData.roles.includes('admin') || currentUserData.roles.includes('organization_admin')) {
            this.userService.getRoles().subscribe(roles => this.roleOptions = roles);
          }
          this.currentUserId = currentUserData.id;
          this.id = +params.get('id') || this.currentUserId;

          this.userService.getUser(this.id, { include_allowed_store_data: 'true' }).pipe(
            tap(user => {
              this.titleService.setTitle(user.uid);
              if (user.organizationId) {
                this.organizationService.addOrganization(user).subscribe();
              }

              if (user.homeStoreId) {
                this.storeService.getStore(user.homeStoreId).subscribe(store => {
                  this.userHomeStore = store;
                });
              }
            }))
            .subscribe({
              next: currentUser => {
                this.user = currentUser;
              },
              error: () => this.router.navigate(['/'])
            });
        });
      });
    });
  }

  openEdit(): void {
    const dialogRef = this.dialog.open(UserFormComponent, {
      data: { user: this.user, roleOptions: this.roleOptions }
    });
    dialogRef.afterClosed().subscribe(result => this.user = result || this.user);
  }

  getRoles() {
    return this.user.roles.length === 0 ? 'N/A' : _.join(this.user.roles, ', ');
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

   switchLang(lang: any) {
    this.currentLang = lang;
    localStorage.setItem('appLang', lang.code);
    this.languageService.setLangToPreferences(lang.code);
    this.translate.use(this.currentLang.code);
  }

  async signOut(): Promise<void> {
    try {
      await this.authService.signOut();
      // noinspection JSIgnoredPromiseFromCall
      await this.router.navigate(["/"]);
    } catch (error) {
      console.error("Error during sign out:", error);
      const errorMsg = this.translate.instant("signOut.error");
      this.toastr.error(errorMsg);
    }
  }
}

